import { useEffect, useState } from 'react';
import { KeyRound, Loader2, RotateCcw, ShieldAlert, Copy, Check } from 'lucide-react';
import * as api from '../../lib/api';
import { useShiftStore } from '../../store/useShiftStore';
import NoFacility from '../../components/attendant/NoFacility';

/**
 * Who can currently get through the gate, and a way to issue a replacement.
 *
 * A tenant who has lost their code previously had no route back short of being
 * let in by hand every visit — the code is only ever shown at reservation, and
 * nothing in the product could mint a new one.
 */
export default function CredentialsPage() {
  const facilityId = useShiftStore((s) => s.facilityId);
  const facilities = useShiftStore((s) => s.facilities);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    if (!facilityId) { setState('no_facility'); return; }
    setState('loading');
    try {
      const { credentials } = await api.gateCredentials(facilityId);
      setRows(credentials);
      setState('ready');
    } catch (err) {
      setError(err.message);
      setState('error');
    }
  };

  useEffect(() => { load(); }, [facilityId]);

  if (state === 'no_facility') return <NoFacility hasAnyFacility={facilities.length > 0} />;

  const reissue = async (row) => {
    if (!window.confirm(
      `Issue a new gate code for ${row.first_name} ${row.last_name} (unit ${row.unit_number})?\n\n`
      + `Their current code ${row.gate_code} stops working immediately.`)) return;
    setBusy(row.lease_public_id);
    try {
      setIssued(await api.reissueCredential(facilityId, row.lease_public_id));
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Gate access</h1>
      <p className="mt-1 text-sm text-slate-600">
        Everyone with a working code at this facility.
      </p>

      {error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}

      {state === 'loading' && (
        <div className="grid place-items-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
        </div>
      )}

      {state === 'ready' && rows.length === 0 && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <KeyRound className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">Nobody has gate access yet</p>
          <p className="mt-1 text-sm text-slate-500">Codes are issued when a space is reserved.</p>
        </div>
      )}

      {state === 'ready' && rows.length > 0 && (
        <ul className="mt-5 space-y-2">
          {rows.map((r) => (
            <li key={r.lease_public_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-900">
                  {r.first_name} {r.last_name}
                  <span className="ml-2 font-normal text-slate-500">unit {r.unit_number}</span>
                </p>
                <p className="mt-0.5 text-xs text-slate-500">{r.email}</p>
                {!r.is_active && (
                  <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[12px] font-bold text-rose-800">
                    <ShieldAlert className="h-3 w-3" aria-hidden="true" />
                    Suspended{r.suspend_reason ? ` — ${r.suspend_reason}` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <code className="rounded-lg bg-slate-100 px-3 py-1.5 font-mono text-sm font-bold tracking-widest text-slate-900">
                  {r.gate_code}
                </code>
                <button type="button" onClick={() => reissue(r)} disabled={busy === r.lease_public_id}
                        className="btn-ghost py-1.5 text-xs">
                  {busy === r.lease_public_id
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    : <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />}
                  New code
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {issued && <IssuedDialog issued={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}

/** The new code, shown once. The QR payload is never stored, only its hash. */
function IssuedDialog({ issued, onClose }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4"
         role="dialog" aria-modal="true" aria-labelledby="issued-title">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <h2 id="issued-title" className="text-lg font-extrabold tracking-tight text-slate-900">
          New code for unit {issued.unit_number}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {issued.tenant}&rsquo;s old code stopped working just now. Give them this one.
        </p>
        <div className="mt-4 flex items-center gap-2">
          <code className="flex-1 rounded-xl bg-slate-100 px-4 py-3 text-center font-mono text-2xl font-bold tracking-[0.3em] text-slate-900">
            {issued.gate_code}
          </code>
          <button type="button" aria-label={copied ? 'Copied' : 'Copy code'}
                  onClick={async () => {
                    await navigator.clipboard.writeText(issued.gate_code);
                    setCopied(true); setTimeout(() => setCopied(false), 2000);
                  }}
                  className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-slate-300 text-brand-600 hover:bg-slate-50">
            {copied ? <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                    : <Copy className="h-4 w-4" aria-hidden="true" />}
          </button>
        </div>
        <button type="button" onClick={onClose}
                className="mt-5 w-full rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-700">
          Done
        </button>
      </div>
    </div>
  );
}
